import { useEffect, useMemo, useState } from 'react';
import { Box, TextField, Autocomplete, Button, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDataProvider, useListContext, useNotify } from 'react-admin';
import { ClientDataType } from '../utils/DataTypes';

const ClaimExceptionsFilter = () => {
  const { setFilters } = useListContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [selectedClient, setSelectedClient] = useState<ClientDataType | null>(null);
  const [inputValue, setInputValue] = useState('');

  const [claimId, setClaimId] = useState('');
  const [drugName, setDrugName] = useState('');

  const [clientOptions, setClientOptions] = useState<ClientDataType[]>([]);
  const [claimIdOptions, setClaimIdOptions] = useState<string[]>([]);
  const [drugOptions, setDrugOptions] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setFilters({});
  }, []);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.getOne('ClaimExceptionFilters', { id: 'filters' });
        const filters = data || {};
        setClientOptions(filters.clients || []);
        setClaimIdOptions(filters.claim_ids || []);
        setDrugOptions(filters.drug_names || []);
      } catch (error) {
        notify('Failed to fetch filter options', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();
  }, [dataProvider, notify]);

  const filteredOptions = useMemo(
    () =>
      clientOptions.filter(
        (option) =>
          (option.client_name && option.client_name.toLowerCase().includes(inputValue.toLowerCase())) ||
          (option.client_number && option.client_number.toLowerCase().includes(inputValue.toLowerCase()))
      ),
    [clientOptions, inputValue]
  );

  const applyFilters = () => {
    setFilters({
      client_name: selectedClient?.client_name || '',
      claim_id: claimId,
      drug_name: drugName,
    });
  };

  const clearFilters = () => {
    setSelectedClient(null);
    setClaimId('');
    setDrugName('');
    setFilters({});
  };

  return (
    <Box display="flex" alignItems="flex-end" gap={2} p={2} sx={{ width: 900 }} flexWrap="nowrap">
      <Autocomplete
        options={filteredOptions}
        value={selectedClient || null}
        onChange={(e, newValue) => setSelectedClient(newValue)}
        getOptionLabel={(option: ClientDataType) => `${option.client_name} (${option.client_number})` || ''}
        isOptionEqualToValue={(option: ClientDataType, value: ClientDataType) =>
          option.client_number === value.client_number
        }
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        renderOption={(props, option: ClientDataType) => (
          <li {...props} key={option.client_number}>
            {`${option.client_name} (${option.client_number})`}
          </li>
        )}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Client Name"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ minWidth: 250 }}
        loading={loading}
      />

      <Autocomplete
        freeSolo
        options={[]}
        value={claimId}
        onChange={(e, newValue) => setClaimId(newValue || '')}
        disableClearable={!claimId}
        inputValue={claimId}
        onInputChange={(event, newInputValue) => setClaimId(newInputValue)}
        renderInput={(params) => <TextField {...params} label="Claim ID" size="small" />}
        clearOnEscape
        sx={{ minWidth: 200 }}
      />

      <Autocomplete
        options={drugOptions}
        value={drugName || null}
        onChange={(e, newValue) => setDrugName(newValue || '')}
        renderInput={(params) => <TextField {...params} label="Drug Name" size="small" />}
        sx={{ minWidth: 200 }}
      />

      <Button
        variant="contained"
        onClick={applyFilters}
        startIcon={<SearchIcon />}
        sx={{
          minWidth: 100,
          height: 40,
          px: 2,
          borderRadius: 2,
          fontSize: '0.85rem',
          fontWeight: 500,
          textTransform: 'none',
        }}
      >
        Apply
      </Button>

      <Button
        variant="outlined"
        onClick={clearFilters}
        sx={{
          minWidth: 100,
          height: 40,
          px: 2,
          borderRadius: 2,
          fontSize: '0.85rem',
          fontWeight: 500,
          textTransform: 'none',
        }}
      >
        Clear
      </Button>
    </Box>
  );
};

export default ClaimExceptionsFilter;
